import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { useAppContext } from "../../providers/ProviderAppContainer";
import PageTitle from "../common/PageTitle";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import { getWriteableDate } from "../../utils";
import { EMPTY_VALUE } from "../../constants";
import { TEXT_SUBTITLE } from "../../style";

function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

function shiftMonth(date, delta) {
    return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

function monthBounds(date) {
    const from = new Date(date.getFullYear(), date.getMonth(), 1);
    const to = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return {
        from: getWriteableDate({ date: from, removeTime: true }),
        to: getWriteableDate({ date: to, removeTime: true }),
    };
}

function toDateKey(date) {
    return getWriteableDate({ date, removeTime: true });
}

export default function CalendarMonth() {
    const navigate = useNavigate();
    const { requestAPI } = useAppContext();
    const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()));
    const [summary, setSummary] = useState({});
    const [holidays, setHolidays] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();

    const { from, to } = useMemo(() => monthBounds(viewMonth), [viewMonth]);

    useEffect(() => {
        if (!from || from === EMPTY_VALUE || !to || to === EMPTY_VALUE) return;

        setSummary({});
        setHolidays({});

        requestAPI({
            requestPath: "calendar/summary",
            requestMethod: "GET",
            requestGetQuery: { from, to },
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    setSummary(data || {});
                    setError();
                } else {
                    setError(data?.error || "Couldn't load calendar");
                }
            },
        });

        requestAPI({
            requestPath: "calendar/holidays",
            requestMethod: "GET",
            requestGetQuery: { from, to },
            onRequestFailure: () => setHolidays({}),
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    setHolidays(data || {});
                } else {
                    setHolidays({});
                }
            },
        });
    }, [from, to, requestAPI]);

    const openDay = useCallback(
        (date) => {
            if (!date) return;
            const dateKey = toDateKey(date);
            if (!dateKey || dateKey === EMPTY_VALUE) return;
            navigate(`/calendar/day?date=${dateKey}`);
        },
        [navigate],
    );

    const dateTemplate = useCallback(
        (dateMeta) => {
            const cellDate = new Date(dateMeta.year, dateMeta.month, dateMeta.day);
            const key = toDateKey(cellDate);
            const hasEvents = !dateMeta.otherMonth && summary[key]?.count > 0;
            const holiday = !dateMeta.otherMonth ? holidays[key] : null;

            return (
                <span
                    className="flex flex-column align-items-center justify-content-center gap-1 w-full border-round py-1"
                    style={holiday ? { backgroundColor: "var(--red-50)" } : undefined}
                    title={holiday?.name || undefined}
                >
                    <span className={holiday ? "font-semibold text-red-600" : undefined}>{dateMeta.day}</span>
                    <span className="flex align-items-center justify-content-center gap-1" style={{ minHeight: 6 }}>
                        {holiday ? (
                            <span
                                className="flex-shrink-0"
                                style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "var(--red-500)" }}
                            />
                        ) : null}
                        {hasEvents ? (
                            <span
                                className="flex-shrink-0"
                                style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "var(--primary-color)" }}
                            />
                        ) : null}
                        {!holiday && !hasEvents ? (
                            <span className="flex-shrink-0" style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "transparent" }} />
                        ) : null}
                    </span>
                </span>
            );
        },
        [holidays, summary],
    );

    const monthLabel = viewMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });

    return (
        <div className="flex flex-column h-full overflow-hidden">
            <PageTitle title="Calendar" />
            <div className="px-3 pt-3 flex align-items-center justify-content-between gap-2">
                <Button icon="pi pi-chevron-left" rounded text onClick={() => setViewMonth((prev) => shiftMonth(prev, -1))} />
                <span className={`${TEXT_SUBTITLE} font-semibold`}>{monthLabel}</span>
                <Button icon="pi pi-chevron-right" rounded text onClick={() => setViewMonth((prev) => shiftMonth(prev, 1))} />
            </div>

            <div className="flex-1 min-h-0 overflow-scroll px-3 py-3">
                {loading ? (
                    <Loading message="Loading calendar" />
                ) : error ? (
                    <NoContent error={error} />
                ) : (
                    <Calendar
                        value={null}
                        inline
                        view="date"
                        viewDate={viewMonth}
                        onViewDateChange={(e) => e.value && setViewMonth(startOfMonth(e.value))}
                        onSelect={(e) => openDay(e.value)}
                        dateTemplate={dateTemplate}
                        className="w-full"
                        pt={{
                            panel: { className: "w-full border-none shadow-none" },
                            header: { style: { display: "none" } },
                            table: { className: "w-full" },
                        }}
                    />
                )}
            </div>
        </div>
    );
}
